import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

const formatTimestamp = (timestamp, fallback) => {
  if (!timestamp) return fallback || "Unknown date";

  const parsedDate = new Date(timestamp);
  if (Number.isNaN(parsedDate.getTime())) {
    return fallback || timestamp;
  }

  return parsedDate.toLocaleString();
};

const selectBackupOnKeyDown = (event, onSelect) => {
  if (event.key !== "Enter" && event.key !== " ") {
    return;
  }

  event.preventDefault();
  onSelect();
};

const MIN_IFRAME_HEIGHT = 500;
const PREVIEW_MODAL_VIEWPORT_RATIO = 0.85;
const PREVIEW_CHROME_HEIGHT = 170;

const maxPreviewBodyHeight = () => {
  if (typeof window === "undefined") {
    return MIN_IFRAME_HEIGHT;
  }

  return Math.max(
    MIN_IFRAME_HEIGHT,
    Math.floor(window.innerHeight * PREVIEW_MODAL_VIEWPORT_RATIO) - PREVIEW_CHROME_HEIGHT
  );
};

const DesktopIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);

const MobileIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
    <line x1="12" y1="18" x2="12.01" y2="18" />
  </svg>
);

const VariantBuilderVersionsModal = (props) => {
  const [brokenImages, setBrokenImages] = useState({});
  const [previewingBackupId, setPreviewingBackupId] = useState(null);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [iframeHeight, setIframeHeight] = useState(maxPreviewBodyHeight());

  useEffect(() => {
    if (!props.isOpen) {
      setBrokenImages({});
      setPreviewingBackupId(null);
      setPreviewDevice("desktop");
      setIframeHeight(maxPreviewBodyHeight());
    }
  }, [props.isOpen]);

  useEffect(() => {
    setIframeHeight(maxPreviewBodyHeight());
  }, [previewingBackupId]);

  useEffect(() => {
    const handleResize = () => {
      setIframeHeight((prev) => Math.min(prev, maxPreviewBodyHeight()));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handler = (event) => {
      if (!event.data || event.data.type !== "cf-backup-page-height") {
        return;
      }

      const reportedHeight = Number(event.data.height) || MIN_IFRAME_HEIGHT;
      const boundedHeight = Math.max(
        MIN_IFRAME_HEIGHT,
        Math.min(reportedHeight, maxPreviewBodyHeight())
      );
      setIframeHeight(boundedHeight);
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  useEffect(() => {
    if (!props.isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [props.isOpen]);

  if (!props.isOpen) {
    return null;
  }

  const previewSrc = previewingBackupId
    ? `/websites/${props.websiteId}/cta/${props.ctaId}/variants/${props.variantId}/backups/${previewingBackupId}/page`
    : null;

  const previewingBackup = previewingBackupId
    ? props.backups.find((b) => b.id === previewingBackupId)
    : null;

  const previewUserName = previewingBackup?.user
    ? [previewingBackup.user.name, previewingBackup.user.lastname].filter(Boolean).join(" ")
    : null;

  const modalContent = (
    <div
      className="fixed top-0 left-0 w-full h-full flex items-center justify-center px-6"
      style={{ background: "rgba(15, 23, 42, 0.45)", zIndex: 9999 }}
      onClick={previewingBackupId ? () => setPreviewingBackupId(null) : props.onClose}
    >
      <div
        className={`modal-content hard-center w-full ${previewingBackupId ? "max-w-[1200px] h-[85vh]" : "max-w-[900px] max-h-[80vh]"} flex flex-col bg-white dark:bg-slate-950 !shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header border-slate-200 dark:border-slate-800 flex items-center">
          {previewingBackupId ? (
            <button
              className="dark:text-white flex-shrink-0 mr-3 bg-transparent p-1 cursor-pointer"
              style={{ border: "none", lineHeight: 1 }}
              onClick={() => setPreviewingBackupId(null)}
              type="button"
              aria-label="Back"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                <path d="M10 3L5 8.5M5 8.5L10 14M5 8.5H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          ) : null}

          {previewingBackupId ? (
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {previewingBackup?.user?.avatar && (
                <div
                  className="w-[35px] h-[35px] rounded-full bg-cover bg-center flex-shrink-0"
                  style={{ backgroundImage: `url(${previewingBackup.user.avatar})` }}
                />
              )}
              <div className="min-w-0">
                <h4 className="text-black dark:text-white text-xl font-semibold m-0 truncate">
                  {previewingBackup?.name || "Backup"}
                </h4>
                <p className="text-slate-500 dark:text-slate-400 text-xl m-0">
                  {formatTimestamp(previewingBackup?.timestamp, previewingBackup?.last_modified)}
                  {previewUserName && ` · ${previewUserName}`}
                </p>
              </div>
            </div>
          ) : (
            <h4 className="text-2xl semibold text-black dark:text-white flex-1 m-0">
              Past Versions
            </h4>
          )}

          <button
            className="close dark:text-white flex-shrink-0"
            onClick={previewingBackupId ? () => setPreviewingBackupId(null) : props.onClose}
            type="button"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
              <path d="M12.2996 4.1001L4.09961 12.3001M12.2996 12.3001L4.09961 4.1001" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div
          className={`flex-1 min-h-0 ${previewingBackupId ? "relative overflow-y-auto overflow-x-hidden" : "overflow-y-auto p-6"}`}
          style={{ overscrollBehavior: "contain" }}
        >
          {previewingBackupId ? (
            <React.Fragment>
              <div
                className="h-full min-h-full overflow-y-auto"
                style={{ background: previewDevice === "mobile" ? "#667587" : "transparent" }}
              >
                {previewDevice === "mobile" ? (
                  <div className="flex justify-center items-start py-6" style={{ minHeight: `${MIN_IFRAME_HEIGHT}px` }}>
                    <iframe
                      key={previewingBackupId}
                      src={previewSrc}
                      title="Backup preview"
                      frameBorder="0"
                      scrolling="yes"
                      style={{ width: 375, height: 667, border: "none", display: "block", flexShrink: 0 }}
                    />
                  </div>
                ) : (
                  <iframe
                    key={previewingBackupId}
                    src={previewSrc}
                    title="Backup preview"
                    frameBorder="0"
                    scrolling="yes"
                    style={{
                      width: "100%",
                      height: `${iframeHeight}px`,
                      minHeight: `${MIN_IFRAME_HEIGHT}px`,
                      border: "none",
                      display: "block",
                    }}
                  />
                )}
              </div>

              <div
                className="absolute flex rounded-[3px] overflow-hidden border-solid border-[1px] border-[#e6eff7] bg-white/80 shadow-sm z-10"
                style={{ width: 100, height: 40, bottom: 22, left: 12 }}
              >
                <button
                  type="button"
                  title="Desktop"
                  className={`flex-1 flex items-center justify-center h-full cursor-pointer ${
                    previewDevice === "desktop"
                      ? "bg-[#e6eff7] text-slate-700"
                      : "bg-transparent text-slate-600 hover:bg-slate-50"
                  }`}
                  style={{ border: "none", borderRight: "1px solid #e6eff7" }}
                  onClick={() => setPreviewDevice("desktop")}
                >
                  <DesktopIcon />
                </button>
                <button
                  type="button"
                  title="Mobile"
                  className={`flex-1 flex items-center justify-center h-full cursor-pointer ${
                    previewDevice === "mobile"
                      ? "bg-[#e6eff7] text-slate-700"
                      : "bg-transparent text-slate-600 hover:bg-slate-50"
                  }`}
                  style={{ border: "none" }}
                  onClick={() => setPreviewDevice("mobile")}
                >
                  <MobileIcon />
                </button>
              </div>
            </React.Fragment>
          ) : (
            <React.Fragment>
              {props.loading && (
                <div className="text-center text-xl text-slate-500 py-10">
                  Loading past versions...
                </div>
              )}

              {!props.loading && props.error && (
                <div className="text-center text-xl text-red-600 dark:text-red-400 py-10">
                  {props.error}
                </div>
              )}

              {!props.loading && !props.error && props.backups.length === 0 && (
                <div className="text-center text-xl text-black dark:text-white py-10">
                  No versions in history
                </div>
              )}

              {!props.loading && !props.error && props.backups.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {props.backups.map((backup) => {
                    const selected = props.selectedBackupId === backup.id;

                    return (
                      <div
                        key={backup.id}
                        role="button"
                        tabIndex={0}
                        className={`group text-left cursor-pointer rounded-lg border-[1px] border-solid p-3 transition ${
                          selected
                            ? "border-blue-600 ring-1 ring-blue-600 ring-opacity-100"
                            : "border-slate-200 dark:border-slate-800 hover:border-blue-600"
                        }`}
                        style={{ maxWidth: "300px" }}
                        onClick={() => props.onSelectBackup(backup.id)}
                        onKeyDown={(event) =>
                          selectBackupOnKeyDown(event, () =>
                            props.onSelectBackup(backup.id)
                          )
                        }
                      >
                        {backup.screenshot && !brokenImages[backup.id] ? (
                          <div className="relative">
                            <img
                              src={backup.screenshot}
                              alt="Version screenshot"
                              className="w-full max-h-[200px] object-contain object-top rounded-md bg-slate-100 dark:bg-slate-800"
                              onError={() =>
                                setBrokenImages((previousState) => ({
                                  ...previousState,
                                  [backup.id]: true,
                                }))
                              }
                            />
                            <div className="absolute inset-0 rounded-md bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <button
                                type="button"
                                className="bg-transparent border-[1px] border-solid border-white text-white text-xl px-4 py-2 rounded-md cursor-pointer hover:bg-white/10"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  props.onSelectBackup(backup.id);
                                  setPreviewingBackupId(backup.id);
                                }}
                              >
                                Preview
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="relative">
                            <div className="w-full h-[100px] rounded-md bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 text-xl">
                              No screenshot
                            </div>
                            <div className="absolute inset-0 rounded-md bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <button
                                type="button"
                                className="bg-transparent border-[1px] border-solid border-white text-white text-xl px-4 py-2 rounded-md cursor-pointer hover:bg-white/10"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  props.onSelectBackup(backup.id);
                                  setPreviewingBackupId(backup.id);
                                }}
                              >
                                Preview
                              </button>
                            </div>
                          </div>
                        )}

                        <div className="px-2 pt-[10px] pb-[5px]">
                          <div className="text-xl text-slate-600 dark:text-slate-300">
                            {formatTimestamp(
                              backup.timestamp,
                              backup.last_modified
                            )}
                          </div>

                          {backup.user && (
                            <div className="mt-2 flex items-center gap-3 min-w-0 float-left">
                              {backup.user.avatar && (
                                <div className="w-[25px] h-[25px] overflow-hidden rounded-full relative flex-shrink-0">
                                  <img
                                    src={backup.user.avatar}
                                    alt={backup.user.name || "User"}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              )}
                              <span className="text-xl text-slate-500 dark:text-slate-400 truncate">
                                {[backup.user.name, backup.user.lastname]
                                  .filter(Boolean)
                                  .join(" ")}
                              </span>
                            </div>
                          )}

                          {backup.user && <div className="clear-both" />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </React.Fragment>
          )}
        </div>

        <div className="modal-footer flex-shrink-0 !border-slate-200 dark:!border-slate-800 flex justify-end gap-3">
          <button
            type="button"
            className="btn bg-white border-solid border-[1px] rounded-md !border-slate-200 text-black text-xl hover:bg-blue-100 hover:!border-blue-100 hover:text-blue-600"
            onClick={props.onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`btn border-[1px] border-solid rounded-md text-xl ${
              props.restoring || !props.selectedBackupId
                ? "bg-white border-slate-200 text-gray-300 cursor-not-allowed"
                : "bg-blue-600 border-blue-600 !text-white hover:bg-blue-700 hover:!text-white"
            }`}
            onClick={props.onRestore}
            disabled={props.restoring || !props.selectedBackupId}
          >
            {props.restoring ? "Creating..." : "Restore"}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

VariantBuilderVersionsModal.propTypes = {
  backups: PropTypes.array.isRequired,
  ctaId: PropTypes.number.isRequired,
  error: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRestore: PropTypes.func.isRequired,
  onSelectBackup: PropTypes.func.isRequired,
  restoring: PropTypes.bool.isRequired,
  selectedBackupId: PropTypes.number,
  variantId: PropTypes.number.isRequired,
  websiteId: PropTypes.number.isRequired,
};

export default VariantBuilderVersionsModal;
